import store from "@/store";
import { uploadObjAndNotify } from "@/utils/cesium-common";
import type {
	EarthBrainViewer,
	IGeometry,
	TAnchorType,
	TGeometryResult,
	TPipeTwoPointParams,
	TRetainingWallTwoPointParams,
	TSolidMeshSavedData,
} from "earthbrain-viewer";
import { md5 } from "hash-wasm";
import { type IObject, ObjectType } from "../types";
import {
	BOX_SOLID_MESH_LATEST_VERSION,
	CYLINDER_SOLID_MESH_LATEST_VERSION,
	SPHERE_SOLID_MESH_LATEST_VERSION,
	SolidMeshObjectAdapter,
} from "./SolidMeshObjectAdapter";
import {
	type IBoxObjectMetaData,
	type IShapeObjectMetaData,
	type ISolidMeshObjectData,
	type ISolidMeshObjectDataForDB,
	type ShapeConfigSavedData,
	type TClientAttributes,
	type TSimpleStructureType,
	isParametricStructure,
} from "./types";

const MIN_SCALE_ABS = 0.1;

/**
 * SolidMesh Object class
 * Implements IObject interface following the same pattern as RoadObject
 */
export class SolidMeshObject implements IObject, ISolidMeshObjectData {
	readonly type: ObjectType.SOLID_MESH = ObjectType.SOLID_MESH;
	private _id: string;
	private _viewer: EarthBrainViewer | null;
	md5: string;
	name: string;
	isShow: boolean;
	isOpen: boolean;
	isLoaded: boolean;
	associatedTerrain: { id: number; terrainType: string };
	metaData: IShapeObjectMetaData;

	constructor(viewer?: EarthBrainViewer) {
		const defaults = this.default;
		this._id = defaults.id;
		this._viewer = viewer ?? null;
		this.metaData = { ...defaults.metaData };
		this.md5 = defaults.md5;
		this.name = defaults.name;
		this.isShow = false;
		this.isOpen = false;
		this.isLoaded = false;
		this.associatedTerrain = { ...defaults.associatedTerrain };
	}

	get id(): string {
		return this._id;
	}

	set id(value: string) {
		this._id = value;
	}

	/**
	 * Create a SolidMeshObject from viewer by solidMeshId
	 * Used after creation in viewer to save to store
	 */
	static createFromViewer(
		viewer: EarthBrainViewer,
		solidMeshId: string,
		options?: { structureType?: TSimpleStructureType },
	): SolidMeshObject {
		const obj = new SolidMeshObject(viewer);
		obj.id = solidMeshId;

		// Get SavedData from viewer
		const savedData = viewer.getSolidMeshJSON(solidMeshId);
		if (!savedData) {
			throw new Error(`SolidMesh ${solidMeshId} not found in viewer`);
		}

		// Convert to MetaData (id and name are stored separately, not in metaData)
		obj.metaData = SolidMeshObjectAdapter.savedDataToObjectMetaData(savedData, {
			structureType: options?.structureType,
		});
		// Default name is empty (name is not stored in viewer)
		obj.name = "";
		obj.isLoaded = true;
		obj.isShow = true;

		return obj;
	}

	get default(): ISolidMeshObjectData {
		return {
			type: ObjectType.SOLID_MESH,
			id: "",
			md5: "",
			name: "",
			associatedTerrain: { id: 0, terrainType: "" },
			metaData: {
				shapeConfig: {
					shapeType: "box",
					localCenter: { x: 0, y: 0, z: 0 },
					wgs84Center: { x: 0, y: 0, z: 0 },
					translation: { x: 0, y: 0, z: 0 },
					quaternion: { x: 0, y: 0, z: 0, w: 1 },
					scale: { x: 1, y: 1, z: 1 },
					dimensions: { x: 1, y: 1, z: 1 },
					color: { red: 0.5, green: 0.5, blue: 0.5, alpha: 1 },
				},
				attributes: {
					anchorType: "bottom-nw",
				},
			} satisfies IBoxObjectMetaData,
		};
	}

	// Convenience getters for shapeConfig and attributes
	get shapeConfig(): ShapeConfigSavedData {
		return this.metaData.shapeConfig;
	}

	get attributes(): TClientAttributes {
		return this.metaData.attributes;
	}

	/**
	 * Update object with new data
	 */
	async update(
		data: Partial<ISolidMeshObjectData>,
		updateInViewer = false,
	): Promise<void> {
		this._id = data.id ?? this._id;
		this.md5 = data.md5 ?? this.md5;
		this.name = data.name ?? this.name;

		if (data.associatedTerrain) {
			this.associatedTerrain = {
				...this.associatedTerrain,
				...data.associatedTerrain,
			};
		}

		if (data.metaData) {
			// `as` required: spreading two discriminated-union shapeConfigs loses
			// the discriminant, so `satisfies` cannot narrow the result type.
			this.metaData = {
				shapeConfig: {
					...this.metaData.shapeConfig,
					...data.metaData.shapeConfig,
				},
				attributes: {
					...this.metaData.attributes,
					...data.metaData.attributes,
				},
			} as IShapeObjectMetaData;
		}

		if (updateInViewer && this.isLoaded) {
			await this.updateSettingsInViewer();
		}
	}

	/**
	 * Load solid mesh in viewer
	 */
	async loadInViewer(): Promise<void> {
		try {
			const viewer = this.getViewer();
			const savedData = this.getViewerJSON();
			const result = await viewer.loadSolidMeshJSON(savedData);
			this._id = result.id ?? this._id;
			this.isLoaded = true;
			this.updateFromViewer();
			this.isShow = true;
		} catch (e: unknown) {
			console.error("Failed to load SolidMesh in viewer:", e);
			this.isShow = false;
			this.isLoaded = false;
		}
	}

	/**
	 * Get JSON data for viewer (TSolidMeshSavedData format)
	 * id at top level, shapeType is inside shapeConfig
	 * Note: rotation and name are client-only, visibility is managed via primitive.show
	 */
	private getViewerJSON(): TSolidMeshSavedData {
		const id = this._id;
		const attributes = {
			anchorType: this.metaData.attributes.anchorType,
			...(this.metaData.attributes?.baselineQuaternion
				? {
						baselineQuaternion: {
							x: this.metaData.attributes.baselineQuaternion.x,
							y: this.metaData.attributes.baselineQuaternion.y,
							z: this.metaData.attributes.baselineQuaternion.z,
							w: this.metaData.attributes.baselineQuaternion.w,
						},
					}
				: {}),
			...(this.metaData.attributes?.bottomZ !== undefined
				? { bottomZ: this.metaData.attributes.bottomZ }
				: {}),
		};
		// narrow on shapeType so the union distributes over TSolidMeshSavedData
		const shapeConfig = this.metaData.shapeConfig;
		switch (shapeConfig.shapeType) {
			case "box":
				return { id, shapeConfig, attributes };
			case "sphere":
				return { id, shapeConfig, attributes };
			case "cylinder":
				return { id, shapeConfig, attributes };
		}
	}

	/**
	 * Get viewer instance. Prefers DI'd viewer; falls back to store for objects
	 * created without explicit viewer (e.g. ObjectFactory → new + update path).
	 */
	private getViewer(): EarthBrainViewer {
		if (this._viewer) return this._viewer;
		const viewer = store.getters.viewer as EarthBrainViewer | undefined;
		if (!viewer) {
			throw new Error("Viewer not available");
		}
		return viewer;
	}

	/**
	 * Update settings in viewer
	 */
	private async updateSettingsInViewer(): Promise<void> {
		const viewer = this.getViewer();

		// Update transparency (use color.alpha)
		await viewer.setSolidMeshTransparency(
			this.id,
			this.shapeConfig.color.alpha,
		);

		// Update color
		const color = this.shapeConfig.color;
		await viewer.setSolidMeshColor(this.id, [
			color.red,
			color.green,
			color.blue,
		]);

		// Update visibility (use isShow, not attributes - same as other objects)
		viewer.setSolidMeshVisibility(this.id, this.isShow);

		this.updateFromViewer();
	}

	/**
	 * Update object from viewer state.
	 * Preserve client-only fields such as structureType and UI rotation cache.
	 */
	updateFromViewer(): void {
		if (!this.isLoaded) return;

		const viewer = this.getViewer();

		// Get SavedData from viewer
		const savedData = viewer.getSolidMeshJSON(this.id);
		if (!savedData) return;

		// Preserve client-only fields when updating from viewer
		const currentStructureType = this.metaData.attributes.structureType;
		const currentRotation = this.metaData.attributes.rotation;

		// Convert to MetaData (id is stored separately)
		this.metaData = SolidMeshObjectAdapter.savedDataToObjectMetaData(
			savedData,
			{
				structureType: currentStructureType,
			},
		);
		if (currentRotation) {
			this.metaData.attributes.rotation = { ...currentRotation };
		}
	}

	/**
	 * Set visibility
	 * Note: visibility is local only (same as other objects), not saved to server
	 */
	async setVisibility(visible: boolean): Promise<boolean> {
		if (visible && !this.isLoaded) {
			await this.loadInViewer();
			if (!this.isLoaded) return false;
		}

		const viewer = this.getViewer();
		viewer.setSolidMeshVisibility(this.id, visible);
		this.isShow = visible;
		return true;
	}

	/**
	 * Start editing
	 */
	startEdit(): void {
		// Load if not loaded
		if (!this.isLoaded) {
			this.loadInViewer()
				.then(() => {
					if (!this.isLoaded) return;
					this.ensureVisibleAndStartEdit();
				})
				.catch((error) => {
					console.error("Failed to load SolidMesh for editing:", error);
				});
			return;
		}
		if (!this.isShow) {
			this.setVisibility(true)
				.then(() => {
					this.startSolidMeshEdit();
				})
				.catch((error) => {
					console.error(
						"Failed to set SolidMesh visibility for editing:",
						error,
					);
				});
			return;
		}

		this.startSolidMeshEdit();
	}

	private ensureVisibleAndStartEdit(): void {
		if (this.isShow) {
			this.startSolidMeshEdit();
		} else {
			this.setVisibility(true).then(() => {
				this.startSolidMeshEdit();
			});
		}
	}

	private startSolidMeshEdit(): void {
		const viewer = this.getViewer();
		this.fitToView();
		// Tracks one mouse-drag: set on first "Translating", cleared on drag end
		let dragInProgress = false;
		viewer.startSolidMeshEdit(this.id, {
			onProgress: async (status?: string) => {
				try {
					// Mark dragging when viewer reports translating
					if (status === "Translating") {
						// "Translating" fires on every mousemove — capture the pre-drag Z
						// only once at drag start, otherwise the frozen display Z would
						// track the moving anchor instead of staying fixed
						const isDragStart = !dragInProgress;
						dragInProgress = true;
						await store.dispatch("solidMesh/setIsDraggingSolidMesh", true);
						if (isDragStart) {
							try {
								const anchor = await viewer.getSolidMeshAnchorLocal(this.id);
								await store.dispatch(
									"solidMesh/setPreDragLocalZ",
									Number(anchor.z),
								);
							} catch (e: unknown) {
								// ignore anchor read errors
							}
						}
					} else if (status === "Operation Ended") {
						// Drag finished (viewer LEFT_UP) — clear dragging so panels
						// run their one-shot viewer→form sync
						dragInProgress = false;
						await store.dispatch("solidMesh/setIsDraggingSolidMesh", false);
						await store.dispatch("solidMesh/setPreDragLocalZ", undefined);
					}
				} catch (e: unknown) {
					console.warn("[SolidMesh] onProgress drag state update failed:", e);
				}
				this.updateFromViewer();
			},
			onFinish: async () => {
				// Clear dragging state on finish
				try {
					await store.dispatch("solidMesh/setIsDraggingSolidMesh", false);
					await store.dispatch("solidMesh/setPreDragLocalZ", undefined);
				} catch (e: unknown) {
					console.warn("[SolidMesh] onFinish drag state cleanup failed:", e);
				}
				this.updateFromViewer();
			},
			onCancel: async () => {
				// Clear dragging state on cancel
				try {
					await store.dispatch("solidMesh/setIsDraggingSolidMesh", false);
					await store.dispatch("solidMesh/setPreDragLocalZ", undefined);
				} catch (e: unknown) {
					console.warn("[SolidMesh] onCancel drag state cleanup failed:", e);
				}
				this.updateFromViewer();
			},
		});
	}

	private async syncManualNormalizedShapeToViewer(): Promise<void> {
		if (isParametricStructure(this.metaData.attributes.structureType)) {
			return;
		}

		const viewer = this.getViewer();
		const saved = viewer.getSolidMeshJSON(this.id);
		const sc = saved.shapeConfig;

		const anchor = await this.getLocalAnchorPoint();
		await viewer.loadSolidMeshJSON({
			id: this.id,
			shapeConfig: {
				...sc,
				dimensions: {
					x: sc.dimensions.x * sc.scale.x,
					y: sc.dimensions.y * sc.scale.y,
					z: sc.dimensions.z * sc.scale.z,
				},
				scale: { x: 1, y: 1, z: 1 },
			},
			attributes: saved.attributes,
		} as TSolidMeshSavedData);

		await viewer.setLocalPosition(this.id, [anchor.x, anchor.y, anchor.z]);
		this.updateFromViewer();
	}

	/**
	 * End editing
	 */
	async endEdit(): Promise<void> {
		if (!this.isLoaded) return;
		if (await this.finishEditInteraction()) {
			await this.syncManualNormalizedShapeToViewer();
		}
		this.updateFromViewer();
		const propsJSON = await this.toJSON();
		const siteId = store.state.site?.site_uuid;
		const isNew = !store.state.object.objects.some(
			(obj) => obj.id === this._id,
		);
		try {
			if (isNew && siteId) {
				await store.dispatch("object/createObj", { siteId, obj: this });
			} else {
				await uploadObjAndNotify(propsJSON, this.type);
			}
		} catch (error: unknown) {
			// Only clean up newly created objects on save failure.
			// For existing objects, keep local state so the user can retry.
			if (isNew) {
				this.deleteObject();
				store.commit("object/removeObject", this.id);
			}
			throw error;
		}
	}

	/** Only end the editing input; do not save the edited content. */
	async finishEditInteraction(): Promise<boolean> {
		const viewer = this.getViewer();
		if (!viewer.isSolidMeshUnderEdit()) return false;
		await viewer.finishSolidMeshEdit();
		return true;
	}

	/**
	 * Delete object
	 */
	deleteObject(): void {
		this.isLoaded = false;
		const viewer = this.getViewer();
		viewer.deleteSolidMesh(this.id);
	}

	/**
	 * Get geometry
	 */
	getGeometry(): IGeometry {
		if (!this.isLoaded) return { vertices: [], indices: [] };
		const viewer = this.getViewer();
		return viewer.getSolidMeshGeometry(this.id);
	}

	/**
	 * Fit camera to object
	 */
	fitToView(): void {
		if (!this.isLoaded) return;
		const viewer = this.getViewer();
		viewer.fitToSolidMesh(this.id);
	}

	/**
	 * Set transparency
	 */
	async setTransparency(transparency: number): Promise<void> {
		if (!this.isLoaded) return;
		const viewer = this.getViewer();
		await viewer.setSolidMeshTransparency(this.id, transparency);
		this.updateFromViewer();
	}

	/**
	 * Set color (0-1 range)
	 */
	async setColor(color: {
		red: number;
		green: number;
		blue: number;
	}): Promise<void> {
		if (!this.isLoaded) return;
		const viewer = this.getViewer();
		await viewer.setSolidMeshColor(this.id, [
			color.red,
			color.green,
			color.blue,
		]);
		this.updateFromViewer();
	}

	/**
	 * Set scale
	 */
	setScale(scale: { x: number; y: number; z: number }): void {
		if (!this.isLoaded) return;
		const viewer = this.getViewer();
		const normalizedScale = this.normalizeScale(scale);
		viewer.scaleSolidMesh([
			normalizedScale.x,
			normalizedScale.y,
			normalizedScale.z,
		]);
		this.updateFromViewer();
	}

	/**
	 * Set absolute dimensions.
	 */
	setDimensions(
		originalDimensions: { x: number; y: number; z: number },
		editedDimensions: { x: number; y: number; z: number },
	): void {
		if (!this.isLoaded) return;
		const normalizedDimensions = {
			x: this.normalizeDimensionValue(editedDimensions.x),
			y: this.normalizeDimensionValue(editedDimensions.y),
			z: this.normalizeDimensionValue(editedDimensions.z),
		};
		if (
			normalizedDimensions.x === undefined ||
			normalizedDimensions.y === undefined ||
			normalizedDimensions.z === undefined
		)
			return;

		const nextScale = {
			x:
				normalizedDimensions.x /
				this.normalizeDimensionBase(originalDimensions.x),
			y:
				normalizedDimensions.y /
				this.normalizeDimensionBase(originalDimensions.y),
			z:
				normalizedDimensions.z /
				this.normalizeDimensionBase(originalDimensions.z),
		};

		const viewer = this.getViewer();
		viewer.scaleSolidMesh([nextScale.x, nextScale.y, nextScale.z]);
		this.updateFromViewer();

		if (!isParametricStructure(this.metaData.attributes.structureType)) {
			this.metaData.shapeConfig.dimensions = {
				x: normalizedDimensions.x,
				y: normalizedDimensions.y,
				z: normalizedDimensions.z,
			};
			this.metaData.shapeConfig.scale = { x: 1, y: 1, z: 1 };
		}
	}

	/**
	 * Set rotation.
	 * @param rotation - Rotation angles in degrees { x: roll, y: pitch, z: heading }
	 * Rotation is treated as a delta from the viewer baseline.
	 */
	setRotation(rotation: { x: number; y: number; z: number }): void {
		if (!this.isLoaded) return;
		const viewer = this.getViewer();
		this.metaData.attributes.rotation = {
			x: rotation.x,
			y: rotation.y,
			z: rotation.z,
		};
		viewer.rotateSolidMeshFromBaseline(this.id, [
			rotation.x,
			rotation.y,
			rotation.z,
		]);
		this.bumpRotationStamp();
	}

	/**
	 * Notify panels observing this mesh that its rotation changed
	 * (replaces the former 150ms rotation polling).
	 */
	private bumpRotationStamp(): void {
		store
			.dispatch("solidMesh/bumpSolidMeshRotationStamp", this.id)
			.catch((e: unknown) => {
				console.warn("[SolidMesh] rotation stamp dispatch failed:", e);
			});
	}

	/**
	 * Atomically update a retaining wall from its two top-edge basis points.
	 * Deterministic: the confirmed state is computed from the input alone,
	 * so identical input always restores the identical position.
	 * @returns Applied geometry, or undefined if not loaded
	 */
	async updateRetainingWallFromTwoPoints(
		params: TRetainingWallTwoPointParams,
	): Promise<TGeometryResult | undefined> {
		if (!this.isLoaded) return undefined;
		const viewer = this.getViewer();
		const result = await viewer.updateRetainingWallFromTwoPoints(
			this.id,
			params,
		);
		this.updateFromViewer();
		return result;
	}

	/**
	 * Atomically update a pipe from its two axis endpoints.
	 * Same contract as updateRetainingWallFromTwoPoints.
	 * @returns Applied geometry, or undefined if not loaded
	 */
	async updatePipeFromTwoPoints(
		params: TPipeTwoPointParams,
	): Promise<TGeometryResult | undefined> {
		if (!this.isLoaded) return undefined;
		const viewer = this.getViewer();
		const result = await viewer.updatePipeFromTwoPoints(this.id, params);
		this.updateFromViewer();
		return result;
	}

	private normalizeScale(scale: { x: number; y: number; z: number }): {
		x: number;
		y: number;
		z: number;
	} {
		return {
			x: this.clampScaleValue(scale.x),
			y: this.clampScaleValue(scale.y),
			z: this.clampScaleValue(scale.z),
		};
	}

	private clampScaleValue(value: number): number {
		if (!Number.isFinite(value)) return MIN_SCALE_ABS;
		const absValue = Math.abs(value);
		if (absValue < MIN_SCALE_ABS) {
			return value < 0 ? -MIN_SCALE_ABS : MIN_SCALE_ABS;
		}
		return value;
	}

	private normalizeDimensionBase(value: number): number {
		if (!Number.isFinite(value)) return 1;
		if (Math.abs(value) < Number.EPSILON) return 1;
		return value;
	}

	private normalizeDimensionValue(value: number): number | undefined {
		if (!Number.isFinite(value)) return undefined;
		if (value <= 0) return undefined;
		return value;
	}

	/**
	 * Set anchor type
	 */
	async setAnchorType(anchorType: TAnchorType): Promise<void> {
		if (!this.isLoaded) return;
		const viewer = this.getViewer();
		await viewer.setSolidMeshAnchorType(this.id, anchorType);
		this.updateFromViewer();
	}

	/**
	 * Get local anchor point position
	 */
	async getLocalAnchorPoint(): Promise<{ x: number; y: number; z: number }> {
		const viewer = this.getViewer();
		const anchor = await viewer.getSolidMeshAnchorLocal(this.id);
		return { x: anchor.x, y: anchor.y, z: anchor.z };
	}

	/**
	 * Set position in local coordinates (anchor based on anchorType)
	 */
	async setLocalPosition(position: {
		x: number;
		y: number;
		z: number;
	}): Promise<void> {
		if (!this.isLoaded) return;
		const viewer = this.getViewer();
		await viewer.setLocalPosition(this.id, [
			position.x,
			position.y,
			position.z,
		]);
		this.updateFromViewer();
	}

	/**
	 * Update name (client-only, not stored in viewer)
	 */
	setName(name: string): void {
		this.name = name;
	}

	/**
	 * Convert to JSON for saving (versioned)
	 * Note: id is stored at top-level, NOT in metaData
	 * Note: localCenter and associatedTerrain are omitted
	 */
	async toJSON(): Promise<ISolidMeshObjectDataForDB> {
		this.updateFromViewer();
		return this.buildJSONForDB(this._id, this.name);
	}

	/**
	 * Build a payload for duplication: new id/cid, name suffixed with " copy",
	 * all other data copied as-is.
	 */
	async toDuplicateJSON(): Promise<ISolidMeshObjectDataForDB> {
		this.updateFromViewer();
		return this.buildJSONForDB(crypto.randomUUID(), `${this.name} copy`);
	}

	/** Build the versioned DB payload with the given id/name */
	private async buildJSONForDB(
		id: string,
		name: string,
	): Promise<ISolidMeshObjectDataForDB> {
		// Omit localCenter from shapeConfig (computed from wgs84Center on load)
		const { localCenter: _, ...shapeConfigWithoutLocalCenter } =
			this.metaData.shapeConfig;
		const shapeConfigForDB = !isParametricStructure(
			this.metaData.attributes.structureType,
		)
			? this.normalizeManualShapeConfig(shapeConfigWithoutLocalCenter)
			: shapeConfigWithoutLocalCenter;
		const shapeQuaternion = this.metaData.shapeConfig.quaternion;

		const baselineQuaternion =
			this.metaData.attributes.baselineQuaternion ??
			(shapeQuaternion
				? {
						x: shapeQuaternion.x,
						y: shapeQuaternion.y,
						z: shapeQuaternion.z,
						w: shapeQuaternion.w,
					}
				: undefined);

		const json = {
			type: this.type,
			id,
			cid: id,
			name,
			metaData: {
				shapeConfig: shapeConfigForDB,
				attributes: {
					...this.metaData.attributes,
					...(baselineQuaternion ? { baselineQuaternion } : {}),
				},
			},
		} satisfies Omit<ISolidMeshObjectDataForDB, "version" | "md5">;

		return await this.toVersionedObjectDataForDB(json);
	}

	private normalizeManualShapeConfig(
		shapeConfig: Omit<ShapeConfigSavedData, "localCenter">,
	): Omit<ShapeConfigSavedData, "localCenter"> {
		return {
			...shapeConfig,
			dimensions: {
				x: shapeConfig.dimensions.x * shapeConfig.scale.x,
				y: shapeConfig.dimensions.y * shapeConfig.scale.y,
				z: shapeConfig.dimensions.z * shapeConfig.scale.z,
			},
			scale: { x: 1, y: 1, z: 1 },
		};
	}

	private async toVersionedObjectDataForDB(
		json: Omit<ISolidMeshObjectDataForDB, "version" | "md5">,
	): Promise<ISolidMeshObjectDataForDB> {
		const shapeType = this.metaData.shapeConfig.shapeType;
		switch (shapeType) {
			case "box": {
				return {
					version: BOX_SOLID_MESH_LATEST_VERSION,
					...json,
					md5: await md5(JSON.stringify(json)),
				};
			}
			case "sphere": {
				return {
					version: SPHERE_SOLID_MESH_LATEST_VERSION,
					...json,
					md5: await md5(JSON.stringify(json)),
				};
			}
			case "cylinder": {
				return {
					version: CYLINDER_SOLID_MESH_LATEST_VERSION,
					...json,
					md5: await md5(JSON.stringify(json)),
				};
			}
			default: {
				throw new Error(`Unknown shapeType: ${shapeType}`);
			}
		}
	}
}
